import { useEffect, useState } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import { salonInfo } from "@/mocks/content";

interface HeroButtonProps {
  label: string;
  variant: "gold" | "outline";
  onClick: () => void;
}

function HeroButton({ label, variant, onClick }: HeroButtonProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative flex items-center gap-2 whitespace-nowrap cursor-pointer bg-transparent border-none p-0 group"
      style={{
        fontFamily: "'Noto Sans JP', sans-serif",
        fontSize: "0.78rem",
        letterSpacing: "0.22em",
        fontWeight: variant === "gold" ? 500 : 400,
        color: variant === "gold" ? "#D4A373" : "rgba(255,255,255,0.65)",
        transition: "color 0.3s ease",
      }}
    >
      <span
        className="relative pb-px"
        style={{
          color: hovered
            ? variant === "gold" ? "#E8C49A" : "rgba(255,255,255,0.9)"
            : variant === "gold" ? "#D4A373" : "rgba(255,255,255,0.65)",
          transition: "color 0.3s ease",
        }}
      >
        {label}
        {/* Underline */}
        <span
          className="absolute bottom-0 left-0 h-px"
          style={{
            background: variant === "gold" ? "#D4A373" : "rgba(255,255,255,0.5)",
            width: hovered ? "100%" : "0%",
            transition: "width 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        />
      </span>
      {/* Arrow */}
      <span
        className="flex items-center justify-center w-4 h-4"
        style={{
          transform: hovered ? "translateX(4px)" : "translateX(0)",
          opacity: hovered ? 1 : 0.6,
          transition: "transform 0.3s ease, opacity 0.3s ease",
          color: variant === "gold" ? "#D4A373" : "rgba(255,255,255,0.65)",
        }}
      >
        <ArrowRight size={12} />
      </span>
    </button>
  );
}

export default function HeroSection() {
  const [phase, setPhase] = useState<"init" | "panel" | "reveal" | "done">("init");
  const [scrollY, setScrollY] = useState(0);

  // Panel overlay animation sequence
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("panel"), 80);
    const t2 = setTimeout(() => setPhase("reveal"), 900);
    const t3 = setTimeout(() => setPhase("done"), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const parallaxOffset = scrollY * 0.35;
  const textOpacity = Math.max(0, 1 - scrollY / 500);
  const loaded = phase === "reveal" || phase === "done";

  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "600px" }}
    >
      {/* Background Image — fixed parallax */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          transform: `translateY(${parallaxOffset}px) scale(1.12)`,
          willChange: "transform",
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20men%20spa%20interior%20with%20dramatic%20dark%20atmosphere%2C%20private%20treatment%20room%20with%20warm%20amber%20indirect%20lighting%2C%20dark%20stone%20walls%2C%20white%20linen%20bed%2C%20minimalist%20japanese%20aesthetic%2C%20deep%20shadows%20and%20golden%20light%20rays%2C%20ultra%20premium%20wellness%20center%2C%20cinematic%20composition&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="REFINE MEN'S SPA メインビジュアル"
          className="w-full h-full object-cover object-center"
          style={{ filter: "brightness(0.55)" }}
        />
      </div>

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-black/15 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />

      {/* ===== PANEL OVERLAY ANIMATION ===== */}
      {/* Splash panel — luxury intro screen */}
      <div
        className="absolute inset-0 z-20 flex flex-col items-center justify-center overflow-hidden"
        style={{
          pointerEvents: phase === "done" ? "none" : "all",
          transform: phase === "reveal" || phase === "done"
            ? "translateY(-100%)"
            : "translateY(0%)",
          transition: phase === "reveal" || phase === "done"
            ? "transform 1.1s cubic-bezier(0.77, 0, 0.175, 1)"
            : "none",
        }}
      >
        {/* Splash background image */}
        <img
          src="https://readdy.ai/api/search-image?query=ultra%20luxury%20dark%20spa%20ambiance%20with%20deep%20black%20background%2C%20soft%20warm%20golden%20bokeh%20light%20particles%20floating%2C%20abstract%20dark%20marble%20texture%2C%20subtle%20amber%20glow%20from%20below%2C%20cinematic%20moody%20atmosphere%2C%20premium%20japanese%20wellness%20aesthetic%2C%20no%20people%2C%20minimalist%20dark%20luxury&width=1920&height=1080&seq=splash1&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ filter: "brightness(0.6)" }}
        />
        {/* Dark vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.75) 100%)",
          }}
        />
        {/* Thin gold horizontal lines */}
        <div className="absolute inset-0 flex flex-col justify-center pointer-events-none">
          <div
            className="w-full h-px"
            style={{
              background: "linear-gradient(to right, transparent, rgba(212,163,115,0.25), transparent)",
              top: "38%",
              position: "absolute",
            }}
          />
          <div
            className="w-full h-px"
            style={{
              background: "linear-gradient(to right, transparent, rgba(212,163,115,0.15), transparent)",
              top: "62%",
              position: "absolute",
            }}
          />
        </div>

        {/* Logo content */}
        <div
          className="relative z-10 flex flex-col items-center gap-5"
          style={{
            opacity: phase === "panel" || phase === "reveal" ? 1 : 0,
            transform: phase === "panel" || phase === "reveal" ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 0.7s 0.2s ease, transform 0.7s 0.2s ease",
          }}
        >
          {/* Top rule */}
          <div
            className="flex items-center gap-4"
            style={{
              opacity: phase === "panel" ? 1 : 0,
              transition: "opacity 0.5s 0.5s",
            }}
          >
            <span className="w-16 h-px bg-[#D4A373]/60" />
            <span
              className="text-[#D4A373]/80 text-xs tracking-[0.5em] uppercase"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Est. 2020
            </span>
            <span className="w-16 h-px bg-[#D4A373]/60" />
          </div>

          {/* Brand name */}
          <div className="text-center">
            <div
              className="text-[#D4A373] tracking-[0.5em] text-sm font-light mb-2"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              MEN&apos;S SPA
            </div>
            <div
              className="text-white tracking-[0.25em] font-medium"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                textShadow: "0 0 60px rgba(212,163,115,0.3)",
              }}
            >
              REFINE
            </div>
          </div>

          {/* Tagline */}
          <div
            className="text-white/50 text-xs tracking-[0.35em]"
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              opacity: phase === "panel" ? 1 : 0,
              transition: "opacity 0.5s 0.7s",
            }}
          >
            洗練された時間を、あなたに
          </div>

          {/* Loading bar */}
          <div
            className="w-32 h-px overflow-hidden"
            style={{ background: "rgba(255,255,255,0.1)" }}
          >
            <div
              className="h-full bg-[#D4A373]"
              style={{
                width: phase === "panel" ? "100%" : "0%",
                transition: "width 0.7s 0.3s ease",
              }}
            />
          </div>
        </div>
      </div>

      {/* Panel 2 — dark trailing panel, slides up slightly delayed */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 19,
          background: "#0A0A0A",
          transform: phase === "init" || phase === "panel"
            ? "translateY(0%)"
            : "translateY(-100%)",
          transition: phase === "reveal" || phase === "done"
            ? "transform 1.1s 0.14s cubic-bezier(0.77, 0, 0.175, 1)"
            : "none",
        }}
      />

      {/* Content */}
      <div
        className="absolute inset-0 flex items-center z-10"
        style={{ opacity: textOpacity }}
      >
        <div className="w-full max-w-7xl mx-auto px-5 md:px-14">
          <div className="w-full max-w-lg">
            {/* Label */}
            <div
              className="mb-4 flex items-center gap-2"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(22px)",
                transition: "all 0.75s 0.15s cubic-bezier(0.23, 1, 0.32, 1)",
              }}
            >
              <span className="w-5 h-px bg-[#D4A373] flex-shrink-0" />
              <span
                className="text-[#D4A373] tracking-[0.3em] uppercase"
                style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.65rem" }}
              >
                Men&apos;s Esthetic
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className="text-white leading-snug mb-3 whitespace-nowrap"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: "clamp(1.45rem, 6.5vw, 3.6rem)",
                letterSpacing: "0.04em",
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(28px)",
                transition: "all 0.8s 0.28s cubic-bezier(0.23, 1, 0.32, 1)",
                textShadow: "0 4px 30px rgba(0,0,0,0.5)",
              }}
            >
              {salonInfo.tagline}
            </h1>

            {/* Sub Headline */}
            <p
              className="text-[#D4A373]/90 mb-8"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: "clamp(0.65rem, 2.8vw, 0.9rem)",
                letterSpacing: "0.15em",
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(28px)",
                transition: "all 0.8s 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
                wordBreak: "keep-all",
              }}
            >
              {salonInfo.subTagline}
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-row flex-wrap gap-3"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "translateY(0)" : "translateY(28px)",
                transition: "all 0.8s 0.55s cubic-bezier(0.23, 1, 0.32, 1)",
              }}
            >
              <HeroButton
                label="メニューを見る"
                variant="gold"
                onClick={() =>
                  document.querySelector("#menu")?.scrollIntoView({ behavior: "smooth" })
                }
              />
              <HeroButton
                label="ご予約"
                variant="outline"
                onClick={() =>
                  document.querySelector("#cta")?.scrollIntoView({ behavior: "smooth" })
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{ background: "linear-gradient(to top, #0A0A0A, transparent)" }}
      />

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer z-10"
        onClick={() => document.querySelector("#menu")?.scrollIntoView({ behavior: "smooth" })}
        style={{
          opacity: phase === "done" ? 0.6 : 0,
          transition: "opacity 0.8s 0.3s",
        }}
      >
        <span
          className="text-white/50 text-xs tracking-[0.3em]"
          style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          SCROLL
        </span>
        <ChevronDown size={16} className="text-white/50 animate-bounce" />
      </div>
    </section>
  );
}
